// src/components/Common/BookingStatusFilter.jsx
//
// Filtrul după starea programării, ca un „semafor". Aceleași patru puncte în
// ambele stări: închis, cele trei culori stau una sub alta (verde sus, oranj
// la mijloc, roșu jos) iar punctul gri („toți") e strâns la zero; deschis,
// pastila se lungește spre stânga și punctele se rotesc în linie: gri, verde,
// oranj, roșu. Fără text: utilizatorii știu culorile.

import React, { useEffect, useRef, useState } from "react";

import { BOOKING_STATUS_FILTERS } from "../../utils/bookingStatus";
import UIIcon from "./UIIcon";

const byTone = Object.fromEntries(
   BOOKING_STATUS_FILTERS.map((filter) => [filter.tone, filter]),
);

// Ordinea din linie (stânga → dreapta) = ordinea din semafor (sus → jos).
const OPTIONS = [
   { value: null, tone: "all", label: "Toți" },
   { value: byTone.booked.value, tone: "booked", label: byTone.booked.label },
   { value: byTone.pending.value, tone: "pending", label: byTone.pending.label },
   { value: byTone.overdue.value, tone: "overdue", label: byTone.overdue.label },
];

export default function BookingStatusFilter({
   value = null,
   onChange,
   className = "",
   "aria-label": ariaLabel = "Filtru după starea programării",
}) {
   const [open, setOpen] = useState(false);
   const rootRef = useRef(null);

   useEffect(() => {
      if (!open) return undefined;
      const onClickOutside = (event) => {
         if (!rootRef.current?.contains(event.target)) setOpen(false);
      };
      const onKey = (event) => {
         if (event.key === "Escape") setOpen(false);
      };
      document.addEventListener("click", onClickOutside, true);
      document.addEventListener("keydown", onKey);
      return () => {
         document.removeEventListener("click", onClickOutside, true);
         document.removeEventListener("keydown", onKey);
      };
   }, [open]);

   const selected =
      OPTIONS.find((option) => option.value === (value || null)) || OPTIONS[0];
   const isAll = selected.tone === "all";

   const handleDotClick = (event, option) => {
      event.stopPropagation();
      if (!open) {
         setOpen(true);
         return;
      }
      onChange?.(option.value);
      setOpen(false);
   };

   return (
      <div
         ref={rootRef}
         className={
            `bookingStatusFilter ${open ? "is-open" : ""} ${isAll ? "is-all" : ""} ${className}`
               .replace(/\s+/g, " ")
               .trim()
         }
         role="group"
         aria-label={`${ariaLabel}: ${selected.label}`}
         onClick={() => {
            if (!open) setOpen(true);
         }}
      >
         <div className="bookingStatusFilter__lamps">
            {OPTIONS.map((option) => {
               const isActive = option.tone === selected.tone;
               return (
                  <button
                     key={option.tone}
                     type="button"
                     className={
                        `bookingStatusFilter__dotBtn bookingStatusFilter__dotBtn--${option.tone}` +
                        (isActive ? " is-active" : "")
                     }
                     aria-label={option.label}
                     aria-pressed={isActive}
                     tabIndex={open ? 0 : -1}
                     onClick={(event) => handleDotClick(event, option)}
                  >
                     <span className="bookingStatusFilter__dot" aria-hidden="true" />
                  </button>
               );
            })}
         </div>

         {/* X-ul de închidere, vizibil doar când e deschis. */}
         <button
            type="button"
            className="bookingStatusFilter__close"
            aria-label="Închide filtrul"
            tabIndex={open ? 0 : -1}
            onClick={(event) => {
               event.stopPropagation();
               setOpen(false);
            }}
         >
            <UIIcon name="close" className="bookingStatusFilter__closeIcon" />
         </button>
      </div>
   );
}
